import React from "react"
import { PageProps } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import classNames from "classnames"
import Layout from "../../../components/Layout/Layout"
import SEO from "../../../components/seo"
import styles from "./ai-sales.module.scss"
import SecondTitle from "../../../components/UI/SecondTitle/SecondTitle"

const objectiveItems = [
	"AI consultant for shoppers - accessible via mobile browser, with voice and text input;",
	"An administrative dashboard for stores - with the ability to customize scripts, manage content and track analytics.",
]

const AISales: React.FC<PageProps> = ({ location }) => {
	const intl = useIntl()

	return (
		<Layout location={location}>
			<SEO
				language={intl.locale}
				description={intl.formatMessage({ id: "projects-new.list.ai.description" })}
				title={intl.formatMessage({ id: "projects-new.list.ai.metatitle" })}
				pathname={location.pathname}
			/>
			{/* Banner Section */}

			<SecondTitle desktopImage="/images/projects/ai-sales/ampl-main.jpg" title="AI Sales Conversations That Sound Human, Convert Like Experts" position="right center" back="white" />

			<div className={styles.aiSalesPageRoot}>
				{/* About Project Section */}
				<section className={styles.aboutSection}>
					<div className={styles.aboutSectionContainer}>
						<div className={styles.aboutSectionTextBlock}>
							<p className={styles.aboutSectionText}>
								Our task is to develop an AI-based voice consultant capable of
								replacing or complementing the work of salespeople, and an
								administrative system to manage its behavior, scenarios and
								analyze its effectiveness in real time.
							</p>
						</div>
						<div className={styles.aboutSectionInfo}>
							<div className={styles.aboutSectionInfoRow}>
								<span className={styles.aboutSectionInfoLabel}>Client</span>
								<span className={styles.aboutSectionInfoValue}>
									Private Investor (USA)
								</span>
							</div>
							<div className={styles.aboutSectionInfoRow}>
								<span className={styles.aboutSectionInfoLabel}>Expertise</span>
								<span className={styles.aboutSectionInfoValue}>
									Full-cycle development
									<br />
									Design support
									<br />
									Analytics
								</span>
							</div>
							<div className={styles.aboutSectionInfoRow}>
								<span className={styles.aboutSectionInfoLabel}>Date</span>
								<span className={styles.aboutSectionInfoValue}>2024-2025</span>
							</div>
						</div>
					</div>
					<img
						className={`${styles.aboutSectionImage} ${styles.sectionImage}`}
						src="/images/projects/ai-sales/ampl-screens.jpg"
						alt="Platform screens"
					/>
				</section>

				{/* Challenge Section */}
				<section className={styles.challengeSection}>
					<div className={styles.challengeTextBlock}>
						<h2>Challenge</h2>
						<p className={styles.challengeDescription}>
							In offline retail, shoppers face a lack of personalized
							assistance, especially during rush hour or in stores with limited
							staff.
						</p>
					</div>
					<div>
						<h3 className={styles.challengeSubtitle}>
							Classic chatbots don't do the job:
						</h3>
						<div className={styles.cardList}>
							<div className={styles.cardListItem}>
								<div className={styles.cardListIcon}></div>
								they are templated
							</div>
							<div className={styles.cardListItem}>
								<div className={styles.cardListIcon}></div>
								they are not well adapted to offline environments
							</div>
							<div className={styles.cardListItem}>
								<div className={styles.cardListIcon}></div>
								they are not scalable for different scenarios and locations
							</div>
						</div>
					</div>
				</section>

				{/* Objective Section */}
				<section className={styles.objectiveSection}>
					<div className={styles.objectiveSectionTitleBlock}>
						<h2>Objective</h2>
						<div className={styles.objectiveSectionSubtitle}>
							We were tasked with developing and launching an MVP version
							capable of demonstrating the key value of the product in a
							real-world environment.
						</div>
						<div className={styles.objectiveSectionSubtitle}>
							The MVP was to include two full-fledged interfaces:
						</div>
					</div>
					<ul className={styles.objectiveSectionList}>
						{objectiveItems.map((text, index) => (
							<li key={index} className={styles.objectiveSectionItem}>
								<div className={styles.objectiveSectionItemNumber}>{`0${index + 1
									}`}</div>
								<div className={styles.objectiveSectionItemText}>{text}</div>
							</li>
						))}
					</ul>
					<img
						className={`${styles.sectionImage}`}
						src="/images/projects/ai-sales/ampl-screens-2.jpg"
						alt="Platform screens"
					/>
				</section>

				{/* Competitive Analysis Section */}
				<section className={styles.competitiveSection}>
					<div className={styles.competitiveSectionTitleBlock}>
						<h2>Competitive analysis</h2>
						<div className={styles.competitiveSectionSubtitle}>
							We analyzed a number of existing solutions in the field of voice
							and text assistants for retail - from classic chatbots to digital
							kiosks and AI-based embedded systems.
						</div>
					</div>

					<div className={styles.competitorsBlock}>
						<div className={styles.competitorsBlockCards}>
							{/* Strengths Section */}
							<div className={styles.competitorsBlockCard}>
								<h3>Strengths of current solutions:</h3>
								<ul>
									<li>
										Integration with loyalty systems and shopper analytics;
									</li>
									<li>Partial automation of repetitive requests.</li>
								</ul>
							</div>

							{/* Weaknesses Section */}
							<div className={styles.competitorsBlockCard}>
								<h3>Weaknesses:</h3>
								<ul>
									<li>
										High login threshold (need to download an app or undergo
										authorization);
									</li>
									<li>
										Lack of contextual customization for a specific store or
										moment (time of day, season, current promotions);
									</li>
									<li>
										Unfriendly user experience - especially in terms of
										administration interfaces and script customization.
									</li>
								</ul>
							</div>
						</div>
						<img
							className={styles.competitorsBlockImage}
							src="/images/projects/ai-sales/ampl-tables.png"
							alt="Amplitech tables"
						/>
					</div>
				</section>

				{/* Solution Section */}
				<section className={styles.solutionSection}>
					<h2>Solution</h2>
					<div className={styles.solutionSectionText}>
						The AI consultant allows customers to interact directly on their mobile devices via a browser.
					</div>
					<img
						className={`${styles.sectionImage}`}
						src="/images/projects/ai-sales/ampl-solution.jpg"
						alt="Amplitech solution"
					/>
					<div className={styles.solutionSectionModules}>
						<h3>
							We consciously chose web access instead of a native app to
							provide:
						</h3>
						<div className={styles.cardList}>
							<div className={styles.cardListItem}>
								<div className={styles.cardListTitle}>
									Instant entry without downloads
								</div>
								<div>
									The user scans a QR code in the store and immediately starts a
									dialog
								</div>
							</div>
							<div className={styles.cardListItem}>
								<div className={styles.cardListTitle}>
									Minimal login threshold
								</div>
								<div>
									No need to install the app, register or give unnecessary
									permissions
								</div>
							</div>
							<div className={styles.cardListItem}>
								<div className={styles.cardListTitle}>Cross-platform</div>
								<div>
									The solution works equally well on Android and iOS without the
									cost of supporting the two platforms
								</div>
							</div>
						</div>
					</div>
					<img
						className={styles.sectionImage}
						src="/images/projects/ai-sales/ampl-screens-3.jpg"
						alt="Amplitech screens"
					/>
					<div className={styles.solutionSectionModules}>
						<h3>
							The AI consultant runs in the browser and allows the customer to:
						</h3>
						<div className={styles.cardList}>
							<div className={styles.cardListItem}>
								<div>
									Find products, promotions and special offers at a specific
									point of sale (based on geolocation or store ID)
								</div>
							</div>
							<div className={styles.cardListItem}>
								<div>
									Use voice or text commands - for example, “Show current
									discounts” etc.
								</div>
							</div>
							<div className={styles.cardListItem}>
								<div>
									Receive personalized recommendations tailored to the context:
									type of store, time of day, seasonal scenarios or past
									activity
								</div>
							</div>
						</div>
					</div>
					<img
						className={styles.sectionImage}
						src="/images/projects/ai-sales/ampl-screens-4.jpg"
						alt="Amplitech screens"
					/>
					<div className={styles.solutionSectionModules}>
						<h3>Through the admin panel, store employees can:</h3>
						<div className={styles.cardList}>
							<div className={styles.cardListItem}>
								<div>Customize scenarios without a programmer.</div>
							</div>
							<div className={styles.cardListItem}>
								<div>
									Track analytics on interactions (frequency of requests,
									popular topics, exit points).
								</div>
							</div>
							<div className={styles.cardListItem}>
								<div>
									Promptly adapt AI behavior to current tasks (sales, new
									categories, etc.)
								</div>
							</div>
						</div>
					</div>
					<img
						className={styles.sectionImage}
						src="/images/projects/ai-sales/ampl-screens-5.jpg"
						alt="Amplitech screens"
					/>
				</section>

				{/* UI Kit Section */}
				<section className={classNames(styles.uikit)}>
					<h2>UI Kit and design system</h2>
					<div className={styles.uikitText}>
						To ensure visual consistency and rapid scalability of the product,
						we developed design system. <br />
						UI Kit became the basis for accelerated development, allowing the
						team to quickly launch new scenarios and interfaces without loss of
						quality. The design system is used in all internal and pilot
						projects and is ready to scale to partner networks.
					</div>
					<img
						className={styles.sectionImage}
						src="/images/projects/ai-sales/ampl-screens-6.jpg"
						alt="Amplitech screens"
					/>
					<img
						className={styles.sectionImage}
						src="/images/projects/ai-sales/ampl-screens-7.jpg"
						alt="Amplitech screens"
					/>
				</section>

				{/* Result Section */}
				<section className={styles.resultSection}>
					<h2>Result</h2>
					<div className={styles.resultSectionText}>
						We developed and launched an MVP that was piloted in one of the
						federal home appliance chains.
					</div>
					<div
						className={classNames(
							styles.adaptiveTextBlock,
							styles.resultSectionSubtitle
						)}
					>
						Results after 3 weeks of testing:
					</div>
					<div className={classNames(styles.cardList)}>
						<div
							className={classNames(
								styles.resultSectionCard,
								styles.cardListItem
							)}
						>
							<div className={styles.resultSectionCardValue}>40%</div>
							<div className={styles.resultSectionCardText}>
								of shoppers interacted with AI during their visit.
							</div>
						</div>
						<div
							className={classNames(
								styles.cardListItem,
								styles.resultSectionCard
							)}
						>
							<div className={styles.resultSectionCardValue}>22%</div>
							<div className={styles.resultSectionCardText}>
								increase in conversion to purchase by promotional groups.
							</div>
						</div>
						<div
							className={classNames(
								styles.resultSectionCard,
								styles.cardListItem
							)}
						>
							<div className={`${styles.resultSectionImage}`} />
							<div className={styles.resultSectionCardText}>
								employees noted reduced workload and improved control over
								promotions.
							</div>
						</div>
					</div>
				</section>
			</div>
		</Layout>
	)
}

export default AISales
